import { useSearchLoadTemplateQuery } from '@/apis/searchtemplates';
import { NotRenewingContext } from '@/App';
import { SearchTreeNode } from '@/types';
import { hasNotRenewing, parseJSONWithoutException } from '@/util';
import { usePrecacheUserContactPerms } from '@hooks/useCaching';
import { searchTreeBuilderStateAtom } from '@pages/AdvancedSearchListCreatorPage';
import { useSearchTree } from '@pages/AdvancedSearchListCreatorPage/JsonTreeBuilder';
import { mapToSearchTree } from '@pages/AdvancedSearchListCreatorPage/SearchTreeBuilder';
import { CircularProgressLoadingPage } from '@pages/CircularProgressLoadingPage';
import { searchTemplateForCopyAtom } from '@pages/SavedSearchesPage';
import { useAtom } from 'jotai';
import { useContext, useEffect } from 'react';
import { useParams } from 'react-router';
import SearchTemplatePage from '.';

export const SearchTemplatePageLoader = () => {
  const { searchTemplateId } = useParams();

  const { data: searchTemplateData, isFetching: isLoadingSearchTemplate } =
    useSearchLoadTemplateQuery(Number(searchTemplateId));
  const { setNotRenewingPresent } = useContext(NotRenewingContext);

  usePrecacheUserContactPerms();

  // The searchTreeBuilderState variable is used to create a template from a search currently
  // being displayed on AdvancedSearchListCreatorPage. It works very well.
  const [searchTreeBuilderState, setSearchTreeBuilderState] = useAtom(searchTreeBuilderStateAtom);

  // The initialLoadedTree variable is used when loading an existing template tree from the
  // backend. Like the Advanced Search Tree builder, we use useSearchTree, and it is all good.
  const { data: initialLoadedTree, isFetching } = useSearchTree(searchTemplateData?.data);

  // The searchTemplateForCopyValue is when we copy over an existing search template.
  const [searchTemplateForCopyValue, setSearchTemplateForCopyValue] =
    useAtom(searchTemplateForCopyAtom);

  const derivedSearchTemplateJSON = parseJSONWithoutException(
    searchTemplateForCopyValue?.searchTemplateJson ?? ''
  );
  const { data: altLoadedTree, isFetching: altFetching } = useSearchTree(
    derivedSearchTemplateJSON as SearchTreeNode | undefined
  );

  const isFiltersRequiringPreferenceUpdates =
    hasNotRenewing(searchTemplateData?.data) ||
    hasNotRenewing(derivedSearchTemplateJSON) ||
    hasNotRenewing(
      searchTreeBuilderState?.tree
        ? mapToSearchTree(searchTreeBuilderState.tree, searchTreeBuilderState.config, {})
        : undefined
    );

  useEffect(() => {
    return () => {
      setSearchTreeBuilderState(undefined);
      setSearchTemplateForCopyValue(undefined);
    };
  }, [setSearchTreeBuilderState, setSearchTemplateForCopyValue]);

  const defaultLoadedTree =
    searchTreeBuilderState && searchTreeBuilderState.tree
      ? searchTreeBuilderState.tree
      : searchTemplateForCopyValue
        ? altLoadedTree
        : initialLoadedTree;

  const isWaitingForFinalStatus =
    isFetching || altFetching || !defaultLoadedTree || isLoadingSearchTemplate;

  useEffect(() => {
    if (!isWaitingForFinalStatus) {
      setNotRenewingPresent(isFiltersRequiringPreferenceUpdates);
    }
  }, [isFiltersRequiringPreferenceUpdates, isWaitingForFinalStatus, setNotRenewingPresent]);

  return isWaitingForFinalStatus ? (
    <CircularProgressLoadingPage />
  ) : (
    <>
      <SearchTemplatePage
        searchTemplateId={searchTemplateId}
        loadedTree={defaultLoadedTree}
        loadedMetadata={
          searchTemplateId
            ? searchTemplateData?.metadata
            : searchTemplateForCopyValue
              ? {
                  id: undefined,
                  name: '',
                  description: searchTemplateForCopyValue.description,
                  organisations: [],
                }
              : undefined
        }
      />
    </>
  );
};
